import { useCallback, useEffect, useMemo, useState } from "react";
import { IGif, IGifList } from "@/common/services/types";
import { useFeedsStore } from "@/common/store/feedsStore";
import { useGifStore } from "@/common/store/gifStore";
import { useUsersStore } from "@/common/store/storeUsers";
import { useNichesStore } from "@/common/store/storeNiches";
import { AxiosError } from "axios";
import urlPrefetcher from "@/common/utils/urlPrefetcher";

interface IOptions {
  canRunRequest?: boolean;
}

export const usePaginatedFeed = (
  keyParams: (string | number | boolean | undefined)[],
  func: (page?: number) => Promise<IGifList | undefined>,
  { canRunRequest }: IOptions = { canRunRequest: true },
) => {
  const key = keyParams.join(":");

  const feed = useFeedsStore((state) => state.getFeed(key));
  const appendFeed = useFeedsStore((state) => state.appendFeed);
  const updateFeed = useFeedsStore((state) => state.updateFeed);
  const updateGifs = useGifStore((state) => state.updateGifs);
  const getGifById = useGifStore((state) => state.getGifById);
  const updateUsers = useUsersStore((state) => state.updateUsers);
  const updateNiches = useNichesStore((state) => state.updateNiches);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>(null);

  const { ids = [], boostedIds = [], page, pages, total, feedId } = feed || {};

  const fetchRequest = useCallback(async (page?: number) => {
    try {
      setLoading(true);

      const res = await func(page);

      if (res) {
        updateGifs(res.gifs);
        updateUsers(res.users);
        updateNiches(res.niches);

        appendFeed(key, {
          page: res.page,
          pages: res.pages,
          total: res.total,
          feedId: res.feed_id,
          ids: res.gifs.map(gif => gif.id),
          boostedIds: res.gifs.filter(gif => !!gif.cta).map(gif => gif.id),
        });

        urlPrefetcher.run(res.prefetch ?? []);
      }
    } catch (e) {
      const error = e as AxiosError;
      setError(error);
      console.error("Error reading gifs.", error);
    } finally {
      setLoading(false);
    }
  }, [func]);

  const update = useCallback((gifs: IGif[]) => {
    updateFeed(key, {
      ids: gifs.map(gif => gif.id),
    });
  }, [feed]);

  const hasMore = useMemo(() => page && pages ? page < pages : false, [page, pages]);

  const loadMore = useCallback(() => {
    if (hasMore && page) {
      fetchRequest(page + 1);
    }
  }, [page, hasMore, fetchRequest]);

  useEffect(() => {
    if (feed === undefined && canRunRequest) {
      fetchRequest();
    }
  }, [feed, fetchRequest, canRunRequest]);

  const gifs = useMemo(() => {
    const gifs: IGif[] = [];

    ids.forEach(id => {
      const gif = getGifById(id);

      if (gif) {
        gifs.push({
          ...gif,
          cta: !!gif.cta && boostedIds.includes(id) ? gif.cta : undefined,
        });
      }
    });

    return gifs;
  }, [ids, getGifById]);

  return {
    gifs,
    total: total || null,
    feedId,
    loading,
    error,
    hasMore,
    loadMore,
    update,
  };
};
